/**
 * SEO / head manager for the HornDeliver Vite SPA.
 *
 * Because HornDeliver is a single-page app with hash-based routing, crawlers
 * and AI agents that *do* run JavaScript (GPTBot, ClaudeBot, Googlebot,
 * Bingbot, etc.) need the document `<head>` to be updated dynamically as
 * the user navigates. This module:
 *
 *   1. Defines per-route SEO metadata (title, description, OG, canonical,
 *      robots, JSON-LD).
 *   2. Provides a `setSeo()` function that patches the live `<head>`.
 *   3. Exposes a React hook `useSeo()` that reacts to route changes via
 *      the Zustand UI store.
 *
 * The static `index.html` always carries the global defaults so that
 * crawlers without JavaScript still see a fully-formed page.
 */

import { useEffect } from "react";
import { useUiStore } from "@/store/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SeoMeta {
  /** Document title (includes brand suffix). */
  title: string;
  /** Meta description (~155 chars). */
  description: string;
  /** Canonical URL (absolute). */
  canonical: string;
  /** OG type. Defaults to "website". */
  ogType?: string;
  /** OG image (absolute URL). Falls back to the brand logo. */
  ogImage?: string;
  /** Robots directives. Defaults to "index, follow". */
  robots?: string;
  /** Optional keywords. */
  keywords?: string;
  /** Optional JSON-LD blocks to inject (replaces previous route blocks). */
  jsonLd?: Record<string, unknown>[];
  /** Optional hreflang alternates. */
  alternates?: { lang: string; href: string }[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const SITE_URL = "https://HornDeliver.app";
export const SITE_NAME = "HornDeliver";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/horndeliver-logo.png`;
const JSONLD_ROUTE_ID = "route-jsonld";

const BRAND_SUFFIX = "HornDeliver";

// ---------------------------------------------------------------------------
// Head patching utilities
// ---------------------------------------------------------------------------

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string, extra?: Record<string, string>) {
  if (!href) return;
  let el = document.head.querySelector<HTMLLinkElement>(
    `link[rel="${rel}"]`
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  if (extra) {
    for (const [k, v] of Object.entries(extra)) el.setAttribute(k, v);
  }
}

function upsertJsonLd(blocks: Record<string, unknown>[]) {
  // Remove any previous route-level JSON-LD blocks.
  document.head
    .querySelectorAll(`script[data-seo-id="${JSONLD_ROUTE_ID}"]`)
    .forEach((n) => n.remove());
  for (const block of blocks) {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.dataset.seoId = JSONLD_ROUTE_ID;
    s.textContent = JSON.stringify(block);
    document.head.appendChild(s);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function setSeo(meta: SeoMeta) {
  const title = meta.title.includes(BRAND_SUFFIX)
    ? meta.title
    : `${meta.title} — ${BRAND_SUFFIX}`;

  document.title = title;
  upsertMeta("name", "title", title);
  upsertMeta("name", "description", meta.description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", meta.description);
  upsertMeta("property", "og:url", meta.canonical);
  upsertMeta("property", "og:type", meta.ogType ?? "website");
  upsertMeta("property", "og:image", meta.ogImage ?? DEFAULT_OG_IMAGE);
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", meta.description);
  upsertMeta("name", "twitter:image", meta.ogImage ?? DEFAULT_OG_IMAGE);
  upsertMeta("name", "twitter:card", "summary_large_image");

  if (meta.keywords) upsertMeta("name", "keywords", meta.keywords);
  upsertMeta("name", "robots", meta.robots ?? "index, follow, max-image-preview:large");

  upsertLink("canonical", meta.canonical);

  // hreflang alternates — `setSeo` owns ALL hreflang link tags so we remove
  // every existing one (both static from index.html and dynamic from prior
  // route changes) before re-adding. This avoids the duplication bug where
  // the homepage's 8 static hreflang tags would stack up to 16 on revisit.
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((n) => n.remove());
  if (meta.alternates) {
    for (const a of meta.alternates) {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = a.lang;
      el.href = a.href;
      document.head.appendChild(el);
    }
  }

  if (meta.jsonLd && meta.jsonLd.length) upsertJsonLd(meta.jsonLd);
}

// ---------------------------------------------------------------------------
// Shared hreflang alternates — every public, indexable route advertises the
// same set of supported languages. The href is per-route so search engines
// understand that, e.g., the Arabic version of /#/login is /#/login?lang=ar.
// ---------------------------------------------------------------------------

const SUPPORTED_LANGS = [
  { lang: "en", label: "English" },
  { lang: "am", label: "አማርኛ" },
  { lang: "ar", label: "العربية" },
  { lang: "om", label: "Afaan Oromoo" },
  { lang: "so", label: "Soomaali" },
  { lang: "ti", label: "ትግርኛ" },
  { lang: "aa", label: "Qafar" },
] as const;

function hreflangFor(canonicalPath: string): { lang: string; href: string }[] {
  // canonicalPath is the hash-path portion, e.g. "" or "login" or "legal/terms".
  // We advertise ?lang=XX on the same hash-path so each language variant has
  // a distinct, route-accurate URL.
  const hash = canonicalPath ? `#/${canonicalPath}` : "";
  return [
    ...SUPPORTED_LANGS.map((l) => ({
      lang: l.lang,
      href: `${SITE_URL}/${hash}?lang=${l.lang}`,
    })),
    { lang: "x-default", href: `${SITE_URL}/${hash}` },
  ];
}

// ---------------------------------------------------------------------------
// Per-route metadata
// ---------------------------------------------------------------------------

const ROUTE_META: Record<string, SeoMeta> = {
  // ---- Public surfaces --------------------------------------------------
  "": {
    title: "HornDeliver — Delivery & Errands Super-App for the Horn of Africa",
    description:
      "On-demand parcel, food, grocery, pharmacy, water and errand delivery for the Horn of Africa. Pickup & Delivery, in-app Order & Delivery, real-time Tracking, dual Wallets, in-app Chat — all in one installable, offline-capable PWA.",
    canonical: `${SITE_URL}/`,
    keywords:
      "delivery, errands, courier, parcel, food delivery, grocery delivery, pharmacy delivery, water delivery, mobile money, telebirr, mpesa, Horn of Africa, Ethiopia, Djibouti, Eritrea, Somalia, Somaliland, PWA",
    // hreflang alternates are injected dynamically by useSeo() via hreflangFor("").
  },

  login: {
    title: "Sign in to HornDeliver",
    description:
      "Sign in to your HornDeliver account to place Pickup & Delivery orders, track couriers in real time, manage your wallets and chat with merchants and drivers.",
    canonical: `${SITE_URL}/#/login`,
    robots: "index, follow",
  },

  register: {
    title: "Create your HornDeliver account",
    description:
      "Create a free HornDeliver account and start sending parcels, ordering from merchant stores and managing deliveries across the Horn of Africa.",
    canonical: `${SITE_URL}/#/register`,
    robots: "index, follow",
  },

  // ---- Authenticated app (still indexable as concept pages) -------------
  "app": {
    title: "Dashboard — Pickup & Delivery, Orders, Tracking, Wallets",
    description:
      "Your HornDeliver dashboard: active orders, quick actions, wallet balances, recent chats and tracking — everything you need in one mobile-first view.",
    canonical: `${SITE_URL}/#/app`,
    robots: "noindex, follow",
  },

  "app/jobs": {
    title: "My Orders — Pickup & Delivery and in-app Orders",
    description:
      "View and manage all your HornDeliver orders: parcel pickups, in-app merchant orders, delivery status, courier details and proof of delivery.",
    canonical: `${SITE_URL}/#/app/jobs`,
    robots: "noindex, follow",
  },

  "app/jobs/new": {
    title: "Post a Pickup & Delivery — HornDeliver",
    description:
      "Create a new Pickup & Delivery order on HornDeliver. Snap a receipt, set pickup and drop-off, choose on-demand or scheduled, pay from your Debit wallet or Mobile Money.",
    canonical: `${SITE_URL}/#/app/jobs/new`,
    robots: "noindex, follow",
  },

  "app/stores": {
    title: "Stores — Order from merchant stores on HornDeliver",
    description:
      "Browse HornDeliver merchant stores: food, groceries, pharmacy, bottled water, electronics and gifts. Place an in-app order and a courier delivers it.",
    canonical: `${SITE_URL}/#/app/stores`,
    robots: "noindex, follow",
  },

  "app/track": {
    title: "Real-Time Tracking — HornDeliver",
    description:
      "Track your HornDeliver courier in real time: live map, ETA, status timeline and proof-of-delivery media. Works offline with cached positions.",
    canonical: `${SITE_URL}/#/app/track`,
    robots: "noindex, follow",
  },

  "app/wallet": {
    title: "Wallets — Debit & Credit on HornDeliver",
    description:
      "Manage your HornDeliver Debit wallet (top-up balance for payments) and Credit wallet (courier/merchant earnings, withdrawable to Mobile Money).",
    canonical: `${SITE_URL}/#/app/wallet`,
    robots: "noindex, follow",
  },

  "app/chat": {
    title: "Messages — In-app Chat on HornDeliver",
    description:
      "Chat with your courier or merchant in real time. Per-order context, media sharing and offline-queued messages that sync when you reconnect.",
    canonical: `${SITE_URL}/#/app/chat`,
    robots: "noindex, follow",
  },

  "app/subscriptions": {
    title: "Subscriptions — Premium plans on HornDeliver",
    description:
      "Upgrade to a HornDeliver subscription: lower commission, priority dispatch, advanced analytics and exclusive advertising slots for merchants and power users.",
    canonical: `${SITE_URL}/#/app/subscriptions`,
    robots: "noindex, follow",
  },

  "app/profile": {
    title: "Profile — Verified identity, ratings and devices",
    description:
      "Your HornDeliver profile: KYC verification, trust records, ratings, active devices, two-factor authentication and GDPR data export/erasure controls.",
    canonical: `${SITE_URL}/#/app/profile`,
    robots: "noindex, follow",
  },

  "app/settings": {
    title: "Settings — Notifications, security, privacy, language",
    description:
      "Manage notifications, two-factor authentication, active sessions, privacy & data protection, language and appearance (light/dark/system) on HornDeliver.",
    canonical: `${SITE_URL}/#/app/settings`,
    robots: "noindex, follow",
  },

  "app/help": {
    title: "Help & Supports — HornDeliver",
    description:
      "Get help with HornDeliver: in-app live chat, dispute center, knowledge base and escalation paths for trust & safety issues.",
    canonical: `${SITE_URL}/#/app/help`,
    // Authenticated route — must not be indexed. Public help is at /#/legal/support.
    robots: "noindex, follow",
  },

  "app/earnings": {
    title: "Driver Earnings — HornDeliver Credit Wallet",
    description:
      "Courier earnings on HornDeliver: daily, weekly and monthly breakdowns, withdrawable to Mobile Money (Telebirr, CBE Birr, M-Pesa, Evol, Sahay, E-Dahab).",
    canonical: `${SITE_URL}/#/app/earnings`,
    robots: "noindex, follow",
  },

  "app/merchant": {
    title: "Merchant Dashboard — HornDeliver Storefront",
    description:
      "Run your HornDeliver storefront: today's orders, revenue, inventory sync, bestsellers, ad campaigns and subscription tier.",
    canonical: `${SITE_URL}/#/app/merchant`,
    robots: "noindex, follow",
  },

  // ---- Role selection (authenticated, never index) ---------------------
  "role-select": {
    title: "Choose your role — HornDeliver",
    description:
      "Switch between Customer, Driver, Merchant and Admin roles on HornDeliver.",
    canonical: `${SITE_URL}/#/role-select`,
    robots: "noindex, follow",
  },

  // ---- Admin (never index) ---------------------------------------------
  "admin": {
    title: "Admin Console — HornDeliver",
    description: "HornDeliver platform administration console.",
    canonical: `${SITE_URL}/#/admin`,
    robots: "noindex, nofollow",
  },

  // ---- Legal (public, indexable) ---------------------------------------
  "legal/terms": {
    title: "Terms of Service — HornDeliver",
    description:
      "HornDeliver Terms of Service governing the use of the HornDeliver super-app and platform across the Horn of Africa.",
    canonical: `${SITE_URL}/#/legal/terms`,
    robots: "index, follow",
  },

  "legal/privacy": {
    title: "Privacy Policy — HornDeliver",
    description:
      "HornDeliver Privacy Policy: what personal data we collect, why, how long we keep it, and your rights under GDPR-style data protection.",
    canonical: `${SITE_URL}/#/legal/privacy`,
    robots: "index, follow",
  },

  "legal/data-protection": {
    title: "Data Protection — HornDeliver",
    description:
      "HornDeliver data protection commitments: encryption, access control, audit log, breach notification and data subject rights.",
    canonical: `${SITE_URL}/#/legal/data-protection`,
    robots: "index, follow",
  },

  "legal/support": {
    title: "Support Center — HornDeliver Help & Supports",
    description:
      "Get help with HornDeliver: live chat, dispute center, knowledge base, security contact and escalation paths for trust & safety issues.",
    canonical: `${SITE_URL}/#/legal/support`,
    robots: "index, follow",
  },

  // ---- 404 fallback (unknown routes) ------------------------------------
  "__notfound__": {
    title: "Page not found — HornDeliver",
    description:
      "The HornDeliver page you were looking for could not be found.",
    canonical: `${SITE_URL}/404`,
    robots: "noindex, follow",
  },
};

// Routes that should advertise hreflang alternates to search engines.
// Authenticated app/admin routes are noindex, so hreflang is moot there.
const HREFLANG_ROUTES = new Set([
  "",
  "login",
  "register",
  "legal/terms",
  "legal/privacy",
  "legal/data-protection",
  "legal/support",
]);

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export function useSeo() {
  const route = useUiStore((s) => s.route.path);

  useEffect(() => {
    const seg = route.split("/").filter(Boolean);

    // Special-case the role-select pseudo-route (it lives at #/role-select
    // but is matched via seg[0] === "role-select" in App.tsx).
    if (seg[0] === "role-select") {
      const meta = ROUTE_META["role-select"];
      setSeo({ ...meta, jsonLd: [webPageJsonLd(meta)] });
      return;
    }

    // Try exact match first ("app/jobs/new"), then prefix ("app/jobs", "app").
    let key = "";
    let matched = false;
    for (let i = seg.length; i > 0; i--) {
      const candidate = seg.slice(0, i).join("/");
      if (ROUTE_META[candidate]) {
        key = candidate;
        matched = true;
        break;
      }
    }
    // Empty route (homepage) maps to key "".
    if (!matched && seg.length === 0) {
      key = "";
      matched = true;
    }

    // Unknown routes (e.g. #/typo, #/app/unknown) fall through to the 404
    // meta so search engines see a noindex page instead of homepage SEO.
    const meta = matched ? ROUTE_META[key] : ROUTE_META["__notfound__"];

    // Dynamic JSON-LD per route for richer AI understanding.
    const jsonLd: Record<string, unknown>[] = [webPageJsonLd(meta)];

    if (key === "app/jobs/new") {
      jsonLd.push({
        "@context": "https://schema.org",
        "@type": "OrderAction",
        target: `${SITE_URL}/#/app/jobs/new`,
        actionStatus: "PotentialActionStatus",
        agent: { "@id": `${SITE_URL}/#organization` },
      });
    }
    if (key === "legal/support") {
      jsonLd.push({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: meta.title,
        url: meta.canonical,
      });
    }

    // Inject hreflang alternates only for public, indexable routes. Auth
    // routes are noindex so hreflang would be ignored by crawlers anyway.
    const alternates = HREFLANG_ROUTES.has(key)
      ? hreflangFor(key)
      : undefined;

    setSeo({ ...meta, alternates, jsonLd });
  }, [route]);
}

/** Helper: build the per-route WebPage JSON-LD block. */
function webPageJsonLd(meta: SeoMeta): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: meta.title,
    description: meta.description,
    url: meta.canonical,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    inLanguage: "en",
  };
}
